import React from "react";

import { useTranslation } from "react-i18next";

import { ButtonGroup, TextInput as Text } from "@caelor/ui";
import IconPicker from "../../../../../../components/ui/icon-picker/IconPicker";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { useSpacerAndDividerStore } from "../../../../../../stores/use-spacer-and-divider-store";
import {
  ALIGNMENT_OPTIONS,
  BOOLEAN_OPTIONS,
  ICON_SIZE_OPTIONS,
  LINE_WEIGHT_OPTIONS,
  LINE_WIDTH_OPTIONS,
  TEXT_SIZE_OPTIONS,
} from "./options";
import { SPACER_AND_DIVIDER_PROP, SPACER_AND_DIVIDER_TYPE } from "./defaults";

const SpacerAndDividerSettings = () => {
  const { data, setData } = useSpacerAndDividerStore((state) => ({
    data: state.data,
    setData: state.setData,
  }));

  const { t } = useTranslation();

  const hasIcon =
    data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
      SPACER_AND_DIVIDER_TYPE.ONLY_ICON ||
    data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
      SPACER_AND_DIVIDER_TYPE.TEXT_WITH_ICON;

  const hasText =
    data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
      SPACER_AND_DIVIDER_TYPE.TEXT ||
    data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
      SPACER_AND_DIVIDER_TYPE.TEXT_WITH_ICON;

  return (
    <>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("alignment")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("align")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={ALIGNMENT_OPTIONS}
              value={data[SPACER_AND_DIVIDER_PROP.TEXT_ALIGNMENT]}
              onClick={(value) =>
                setData({ [SPACER_AND_DIVIDER_PROP.TEXT_ALIGNMENT]: value })
              }
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>

        <OptionContainer>
          <OptionLabel>{t("icon")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={
                data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
                  SPACER_AND_DIVIDER_TYPE.ONLY_ICON ||
                data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
                  SPACER_AND_DIVIDER_TYPE.TEXT_WITH_ICON
              }
              onClick={(isOn) => {
                const isTextEnabled =
                  data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
                    SPACER_AND_DIVIDER_TYPE.TEXT ||
                  data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
                    SPACER_AND_DIVIDER_TYPE.TEXT_WITH_ICON;

                const type = isOn
                  ? isTextEnabled
                    ? SPACER_AND_DIVIDER_TYPE.TEXT_WITH_ICON
                    : SPACER_AND_DIVIDER_TYPE.ONLY_ICON
                  : isTextEnabled
                  ? SPACER_AND_DIVIDER_TYPE.TEXT
                  : SPACER_AND_DIVIDER_TYPE.ONLY_DIVIDER;

                setData({ [SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE]: type });
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>

        <OptionContainer>
          <OptionLabel>{t("text")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={
                data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
                  SPACER_AND_DIVIDER_TYPE.TEXT ||
                data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
                  SPACER_AND_DIVIDER_TYPE.TEXT_WITH_ICON
              }
              onClick={(isOn) => {
                const isIconEnabled =
                  data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
                    SPACER_AND_DIVIDER_TYPE.ONLY_ICON ||
                  data[SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE] ===
                    SPACER_AND_DIVIDER_TYPE.TEXT_WITH_ICON;

                const type = isOn
                  ? isIconEnabled
                    ? SPACER_AND_DIVIDER_TYPE.TEXT_WITH_ICON
                    : SPACER_AND_DIVIDER_TYPE.TEXT
                  : isIconEnabled
                  ? SPACER_AND_DIVIDER_TYPE.ONLY_ICON
                  : SPACER_AND_DIVIDER_TYPE.ONLY_DIVIDER;

                setData({ [SPACER_AND_DIVIDER_PROP.DIVIDER_TYPE]: type });
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
      {(hasIcon || hasText) && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("content")}</SettingsOptionHeading>
          {hasIcon && (
            <OptionContainer>
              <OptionLabel>{t("icon")}</OptionLabel>
              <OptionContent>
                <IconPicker
                  defaultTab={
                    data[SPACER_AND_DIVIDER_PROP.EMOJI_ENABLED]
                      ? "Emojis"
                      : "Icons"
                  }
                  tabs={["Icons", "Emojis", "Logos"]}
                  icon={
                    data[SPACER_AND_DIVIDER_PROP.EMOJI_ENABLED]
                      ? data[SPACER_AND_DIVIDER_PROP.EMOJI]
                      : data[SPACER_AND_DIVIDER_PROP.DIVIDER_ICON]
                  }
                  onChange={({ value }) => {
                    if (typeof value === "object") {
                      setData({ [SPACER_AND_DIVIDER_PROP.EMOJI]: value });
                      setData({
                        [SPACER_AND_DIVIDER_PROP.EMOJI_ENABLED]: true,
                      });
                    } else {
                      setData({
                        [SPACER_AND_DIVIDER_PROP.DIVIDER_ICON]: value,
                      });
                      setData({
                        [SPACER_AND_DIVIDER_PROP.EMOJI_ENABLED]: false,
                      });
                    }
                  }}
                />
              </OptionContent>
            </OptionContainer>
          )}

          {hasText && (
            <OptionContainer>
              <OptionLabel>{t("title")}</OptionLabel>
              <OptionContent>
                <Text
                  placeholder={t("add")}
                  value={data[SPACER_AND_DIVIDER_PROP.TEXT]}
                  onChange={(value) =>
                    setData({ [SPACER_AND_DIVIDER_PROP.TEXT]: value })
                  }
                />
              </OptionContent>
            </OptionContainer>
          )}
        </SettingsOptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        {hasIcon && (
          <OptionContainer>
            <OptionLabel>{t("icon-size")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={ICON_SIZE_OPTIONS}
                value={data[SPACER_AND_DIVIDER_PROP.ICON_SIZE]}
                options={{ input: true }}
                onClick={(value) =>
                  setData({ [SPACER_AND_DIVIDER_PROP.ICON_SIZE]: value })
                }
                onChange={({ target: { value } }) =>
                  setData({ [SPACER_AND_DIVIDER_PROP.ICON_SIZE]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}

        {hasText && (
          <OptionContainer>
            <OptionLabel>{t("text-size")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={TEXT_SIZE_OPTIONS}
                value={data[SPACER_AND_DIVIDER_PROP.FONT_SIZE]}
                options={{ input: true }}
                onClick={(value) =>
                  setData({ [SPACER_AND_DIVIDER_PROP.FONT_SIZE]: value })
                }
                onChange={({ target: { value } }) =>
                  setData({ [SPACER_AND_DIVIDER_PROP.FONT_SIZE]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}

        <OptionContainer>
          <OptionLabel>{t("line-weight")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={LINE_WEIGHT_OPTIONS}
              value={data[SPACER_AND_DIVIDER_PROP.WEIGHT]}
              options={{ input: true }}
              onClick={(value) =>
                setData({ [SPACER_AND_DIVIDER_PROP.WEIGHT]: value })
              }
              onChange={({ target: { value } }) =>
                setData({ [SPACER_AND_DIVIDER_PROP.WEIGHT]: value })
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("line-width")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={LINE_WIDTH_OPTIONS}
              value={data[SPACER_AND_DIVIDER_PROP.WIDTH]}
              options={{ input: true }}
              onClick={(value) =>
                setData({ [SPACER_AND_DIVIDER_PROP.WIDTH]: value })
              }
              onChange={({ target: { value } }) =>
                setData({ [SPACER_AND_DIVIDER_PROP.WIDTH]: value })
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <OptionContainer>
          <OptionLabel alignToTop>{t("colors")}</OptionLabel>
          <OptionContent>
            <ColorPicker
              hideColorTiles
              value={{
                accent: data[SPACER_AND_DIVIDER_PROP.DIVIDER_COLOR],
                text: data[SPACER_AND_DIVIDER_PROP.DIVIDER_COLOR],
              }}
              onChange={({ accent }) =>
                setData({ [SPACER_AND_DIVIDER_PROP.DIVIDER_COLOR]: accent })
              }
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
    </>
  );
};

export default SpacerAndDividerSettings;
